/**
 * Auto-start a module tour once per browser — safe against context churn.
 *
 * Bug history: depending on the whole `onboarding` object re-ran startTour on
 * every Next click (context value changes), resetting the tour to step 1.
 */

import { useEffect, useRef } from "react";
import { isTourFinished } from "./tourStorage";
import { WELCOME_TOUR_ID } from "./tours";

/**
 * @param {ReturnType<typeof import("./OnboardingContext").useOnboardingOptional>} onboarding
 * @param {string} tourId
 * @param {{ ready?: boolean, waitForWelcome?: boolean, delayMs?: number }} [options]
 */
export function useModuleTourAutoStart(
  onboarding,
  tourId,
  { ready = true, waitForWelcome = true, delayMs = 800 } = {}
) {
  const startedRef = useRef(false);

  useEffect(() => {
    if (!onboarding?.startTour || !tourId) return;
    if (!ready) return;
    if (startedRef.current) return;

    if (waitForWelcome && !isTourFinished(WELCOME_TOUR_ID)) return;

    if (isTourFinished(tourId)) {
      startedRef.current = true;
      return;
    }

    // Wait while another tour is on screen (e.g. welcome or sidebar T&A).
    if (
      onboarding.isActive &&
      onboarding.activeTourId &&
      onboarding.activeTourId !== tourId
    ) {
      return;
    }

    // Already showing this tour — mark started; do not call startTour again.
    if (onboarding.activeTourId === tourId) {
      startedRef.current = true;
      return;
    }

    const t = window.setTimeout(() => {
      if (startedRef.current) return;
      if (isTourFinished(tourId)) {
        startedRef.current = true;
        return;
      }
      if (onboarding.activeTourId === tourId) {
        startedRef.current = true;
        return;
      }
      if (
        onboarding.isActive &&
        onboarding.activeTourId &&
        onboarding.activeTourId !== tourId
      ) {
        return;
      }
      startedRef.current = true;
      onboarding.startTour(tourId);
    }, delayMs);

    return () => window.clearTimeout(t);
  }, [
    onboarding?.startTour,
    onboarding?.isActive,
    onboarding?.activeTourId,
    tourId,
    ready,
    waitForWelcome,
    delayMs,
  ]);
}
